import { create } from "zustand";
import type { Dashboard } from "@/shared/api/types";
import { dashboardsApi } from "@/shared/api/dashboards/api-dashboards";
import { getServerMessage } from "@/shared/lib/apiErrorHandler";

type StatCategory = "полная" | "артикул" | "склейки";

const getCachedDashboards = (supplierId: string): Dashboard[] | undefined => {
  try {
    const storageKey = `dashboards:${supplierId}`;
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed?.data)) return parsed.data;
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (error) {
    console.error("Ошибка чтения дэшбордов из localStorage", error);
  }
  return undefined;
};

const saveDashboards = (supplierId: string, data: Dashboard[]) => {
  try {
    const storageKey = `dashboards:${supplierId}`;
    const snapshot = { data, cachedAt: new Date().toISOString() };
    localStorage.setItem(storageKey, JSON.stringify(snapshot));
  } catch (error) {
    console.error("Ошибка сохранения дэшбордов в localStorage", error);
  }
};

interface DashboardState {
  activeCategory: StatCategory;
  showAll: boolean;
  setActiveCategory: (category: StatCategory) => void;
  toggleShowAll: () => void;
  reset: () => void;

  dashboards: Dashboard[];
  loading: boolean;
  error: string | null;
  fetchDashboards: (supplierId: string) => Promise<void>;
}

const initialState = {
  activeCategory: "полная" as StatCategory,
  showAll: false,
  dashboards: [],
  loading: false,
  error: null,
};

export const useDashboardStore = create<DashboardState>((set, get) => ({
  ...initialState,
  setActiveCategory: (category) => set({ activeCategory: category }),
  toggleShowAll: () => set((state) => ({ showAll: !state.showAll })),
  reset: () =>
    set({
      ...initialState,
      dashboards: get().dashboards,
    }),

  fetchDashboards: async (supplierId: string) => {
    if (!supplierId) {
      set({ dashboards: [], loading: false, error: null });
      return;
    }

    const cachedData = getCachedDashboards(supplierId);
    if (cachedData) {
      set({ dashboards: cachedData, loading: false });
    } else {
      set({ loading: true, error: null });
    }

    try {
      const response = await dashboardsApi
        .getDashboardsQueryOptions({
          supplier_id: supplierId,
          limit: 10,
          sort_by: "time",
          order: "DESC",
        })
        .queryFn({});

      saveDashboards(supplierId, response.data);
      set({ dashboards: response.data, loading: false, error: null });
    } catch (e: unknown) {
      const errorMessage = getServerMessage(e, "Failed to fetch dashboards");
      set({
        error: errorMessage,
        loading: false,
        dashboards: cachedData || [],
      });
      console.error(errorMessage);
    }
  },
}));
